export interface GocardlessConnection {
  id: string;
  company_id: number;
  organisation_id: string | null;
  environment: string;
  status: string;
  connected_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OAuthInitiateResponse {
  url: string;
}

export interface OAuthExchangeRequest {
  code: string;
  state: string;
}

export type MandateStatus = 'active' | 'pending' | 'none';

/**
 * Lookup-style options for the MandateStatus enum, suitable for multi-select filters
 * or dropdowns. Shared across the client-signups table search bar and the bulk-send
 * wizard.
 */
export const MANDATE_STATUS_OPTIONS: Array<{ id: MandateStatus; name: string }> = [
  { id: 'active', name: 'Active' },
  { id: 'pending', name: 'Pending' },
  { id: 'none', name: 'None' },
];

export interface GocardlessClientSignupRow {
  client_id: number;
  first_name: string | null;
  surname: string | null;
  full_name: string | null;
  email: string | null;
  status: MandateStatus;
  has_outstanding_invoices: boolean;
  outstanding_invoice_count: number;
  last_sent_at: string | null;
}

export interface GocardlessSignupStats {
  total_clients: number;
  active_mandates: number;
  pending_mandates: number;
  no_mandate: number;
}

export interface GocardlessClientSignupsResponse {
  rows: GocardlessClientSignupRow[];
  total: number;
  stats: GocardlessSignupStats;
}

export interface BulkSignupFilters {
  mandate_states: ('no_mandate' | 'pending')[];
  has_outstanding_invoice?: boolean;
  has_email?: boolean;
  last_sent_days?: number;
}

// Form-local mandate filter. `true` = "All Users" (include clients with no
// mandate AND with a pending mandate). `false` = "No Mandates" (clients with
// no mandate only). Active-mandate clients are always hard-excluded
// server-side, so "All Users" means "everyone the wizard is allowed to target".
export type MandateFilter = boolean;

export const resolveMandateStates = (allUsers: MandateFilter): BulkSignupFilters['mandate_states'] =>
  allUsers ? ['no_mandate', 'pending'] : ['no_mandate'];

export interface BulkSignupRecipient {
  id: number;
  first_name: string | null;
  surname: string | null;
  email: string;
}

export interface BulkSignupCountResponse {
  count: number;
  recipients?: BulkSignupRecipient[];
}

export interface BulkSignupResult {
  client_id: number;
  status: 'sent' | 'failed';
  stage?: 'gocardless' | 'email';
  message?: string;
}

export interface BulkSignupResponse {
  sent_count: number;
  failed_count: number;
  results: BulkSignupResult[];
}

// ---------------------------------------------------------------------------
// Mandate Matching (TAV-2049)
// ---------------------------------------------------------------------------

export interface GocardlessMandateStatus {
  tag: string;
  name: string;
  /** Hex colour without the leading '#', e.g. "4CAF50". The badge prefixes '#'. */
  colour: string;
}

export interface GocardlessMandateMatch {
  id: string;
  client_id: number;
  client_name: string | null;
}

export interface GocardlessMandate {
  id: string;
  gocardless_mandate_id: string;
  gocardless_customer_id: string | null;
  customer_name: string | null;
  reference: string | null;
  status: GocardlessMandateStatus;
  is_ignored: boolean;
  is_linked: boolean;
  match: GocardlessMandateMatch | null;
  created_at: string;
}

export interface GocardlessMandateListResponse {
  rows: GocardlessMandate[];
  total: number;
  // Inverse-view counter — when listing active, this is the ignored count, and
  // vice-versa. Powers the "X ignored" / "Y active" UI hint.
  counter: number;
}

export interface GocardlessMandateListQuery {
  is_ignored?: boolean;
  limit?: number;
  offset?: number;
  sortBy?: 'created_at' | 'customer_name' | 'reference' | 'status';
  sortOrder?: 'asc' | 'desc';
  filter?: {
    customer_name?: string;
    reference?: string;
  };
}

// Mandate statuses considered non-linkable. Mirrored from the API service. The
// Link button in the matching UI is disabled when the mandate's status tag is
// in this set, and the API returns 400 if a client attempts the link anyway.
export const NON_LINKABLE_MANDATE_TAGS = new Set([
  'cancelled',
  'expired',
  'failed',
  'consumed',
  'blocked',
]);
